import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Navbar from "@/components/Navbar";
import api, { fileUrl, MASCOT } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Plus, Package, Clock, CheckCircle2, Upload, AlertCircle } from "lucide-react";

export const STATUS = {
  draft: { label: "Rascunho", cls: "bg-[#f5ebe1] text-[#9c5f27]", icon: Clock },
  processing: { label: "Processando", cls: "bg-[#fef3c7] text-[#b45309]", icon: Clock },
  ready: { label: "Pronto", cls: "bg-[#dcfce7] text-[#15803d]", icon: CheckCircle2 },
  published: { label: "Publicado", cls: "bg-[#e0edff] text-[#1d4ed8]", icon: CheckCircle2 },
  error: { label: "Erro", cls: "bg-[#fee2e2] text-[#b91c1c]", icon: AlertCircle },
};

export function StatusBadge({ status }) {
  const s = STATUS[status] || STATUS.draft;
  const Icon = s.icon;
  return (
    <span className={`inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-full ${s.cls}`} data-testid={`status-badge-${status}`}>
      <Icon size={12} /> {s.label}
    </span>
  );
}

function thumb(p) {
  const src = p.images?.cover || p.images?.white || p.images?.source?.[0];
  return src ? fileUrl(src) : null;
}

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/products").then(({ data }) => setProducts(data.products)).finally(() => setLoading(false));
  }, []);

  return (
    <div className="min-h-screen mesh-warm">
      <Navbar />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10" data-testid="dashboard-container">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
          <div>
            <h1 className="font-display text-3xl sm:text-4xl font-bold text-[#9c5f27]">Olá, {user?.name?.split(" ")[0]} 👋</h1>
            <p className="mt-2 text-[#5c4634]">Seus anúncios ficam guardados aqui. Bora criar mais um?</p>
          </div>
          <Link to="/novo" className="btn-orange px-6 py-3.5 rounded-full font-semibold flex items-center gap-2 self-start" data-testid="dashboard-new-product">
            <Plus size={18} /> Novo produto
          </Link>
        </div>

        {loading ? (
          <div className="mt-16 text-center text-[#8c735f]">Carregando seus produtos...</div>
        ) : products.length === 0 ? (
          <div className="mt-12 rounded-3xl border-2 border-dashed border-[#e8d8c8] bg-white/60 p-12 text-center" data-testid="dashboard-empty">
            <img src={MASCOT.happy} alt="Solange" className="w-32 h-32 object-cover rounded-full mx-auto animate-float-slow" />
            <h3 className="mt-5 font-display text-xl font-semibold text-[#2e1e10]">Nenhum anúncio ainda</h3>
            <p className="mt-2 text-[#5c4634] max-w-md mx-auto">Envie pelo menos 6 fotos do seu produto e eu monto o anúncio completo pra Shopee em minutos.</p>
            <Link to="/novo" className="btn-orange inline-flex items-center gap-2 mt-6 px-6 py-3.5 rounded-full font-semibold">
              <Upload size={18} /> Criar meu primeiro anúncio
            </Link>
          </div>
        ) : (
          <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5" data-testid="products-grid">
            {products.map((p) => {
              const t = thumb(p);
              return (
                <button
                  key={p.id}
                  onClick={() => navigate(`/produto/${p.id}`)}
                  className="text-left rounded-2xl bg-white border border-[#e8d8c8] overflow-hidden shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all"
                  data-testid={`product-card-${p.id}`}
                >
                  <div className="aspect-square bg-[#fdf8f3] flex items-center justify-center overflow-hidden">
                    {t ? <img src={t} alt={p.title} className="w-full h-full object-cover" /> : <Package size={40} className="text-[#d8c3ad]" />}
                  </div>
                  <div className="p-4">
                    <StatusBadge status={p.status} />
                    <h3 className="mt-2 font-medium text-[#2e1e10] line-clamp-2 text-sm min-h-[2.5rem]">
                      {p.title || (p.status === "processing" ? "Solange está montando..." : "Anúncio sem título")}
                    </h3>
                    {p.category && <p className="mt-1 text-xs text-[#8c735f] line-clamp-1">{p.category}</p>}
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
